import React, { FC, useState } from 'react';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import { BILLING_UNIT_OPTIONS } from '../../utils/billingUnits';
import { useIsOffline } from '../../offline';

/**
 * Add/Edit Pricing Item — extracted from Settings → Service Pricing so other
 * surfaces (the booking modal's "+ Add appointment type") can create catalog
 * items in place. All three Settings modes keep working, keyed off
 * `editingItem`:
 *   - null            → "Add Pricing Item" (create)
 *   - clinic-owned    → "Edit Pricing Item" (update)
 *   - global template → "Customize Default Price" (create clinic override)
 *
 * Catalog writes are OWNER/ADMIN + online-only (callers gate the entry
 * points; the backend @Roles is the real enforcement). A duplicate
 * name+category 409s — surfaced as an in-dialog alert so the user can rename
 * without losing the form.
 */

type Category = 'medication' | 'vaccination' | 'procedure' | 'appointment' | 'other';

export interface PricingItemDialogProps {
  open: boolean;
  onClose: () => void;
  /** null = create; clinic item = edit; global item = customize override. */
  editingItem?: ServiceCatalogItem | null;
  /** Initial category for create mode (e.g. 'appointment' from the booking modal). */
  defaultCategory?: Category;
  /** Lock the category select (booking modal only creates appointment types). */
  lockCategory?: boolean;
  /** Fires after a successful create OR update with the saved item. */
  onSaved?: (item: ServiceCatalogItem, info: { created: boolean }) => void;
  /** An appointment type added from the Schedule: asks for its calendar colour too. */
  withColor?: boolean;
}

interface FormState {
  name: string;
  category: Category;
  basePrice: string;
  unit: string;
  aliases: string;
  idexxTestCodes: string;
  isActive: boolean;
  pricingMethod: 'FIXED' | 'MARKUP';
  unitCost: string;
  markupPercent: string;
  minPrice: string;
  color: string;
}

function itemToForm(item: ServiceCatalogItem | null, defaultCategory: Category): FormState {
  if (!item) {
    return {
      name: '',
      category: defaultCategory,
      basePrice: '',
      unit: '',
      aliases: '',
      idexxTestCodes: '',
      isActive: true,
      pricingMethod: 'FIXED',
      unitCost: '',
      markupPercent: '',
      minPrice: '',
      color: '#4A90D9',
    };
  }
  return {
    name: item.name,
    category: item.category,
    basePrice: String(item.basePrice),
    unit: item.unit || '',
    aliases: Array.isArray(item.aliases) ? item.aliases.join(', ') : '',
    idexxTestCodes: Array.isArray(item.idexxTestCodes) ? item.idexxTestCodes.join(', ') : '',
    isActive: item.isActive,
    pricingMethod: item.pricingMethod === 'MARKUP' ? 'MARKUP' : 'FIXED',
    unitCost: item.unitCost != null ? String(item.unitCost) : '',
    markupPercent: item.markupPercent != null ? String(item.markupPercent) : '',
    minPrice: item.minPrice != null ? String(item.minPrice) : '',
    color: item.color || '#4A90D9',
  };
}

const PricingItemDialogContent: FC<{
  editingItem: ServiceCatalogItem | null;
  defaultCategory: Category;
  lockCategory: boolean;
  onClose: () => void;
  onSaved?: PricingItemDialogProps['onSaved'];
  withColor: boolean;
}> = ({ editingItem, defaultCategory, lockCategory, onClose, onSaved, withColor }) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isOffline = useIsOffline();

  const [formData, setFormData] = useState<FormState>(() => itemToForm(editingItem, defaultCategory));
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createMutation = useMutation(
    (data: Omit<ServiceCatalogItem, 'id' | 'createdAt' | 'updatedAt' | 'clinicId'>) =>
      serviceCatalogService.create(data),
    {
      onSuccess: (item) => {
        queryClient.invalidateQueries(['service-catalog']);
        enqueueSnackbar('Pricing item created', { variant: 'success' });
        onSaved?.(item, { created: true });
        onClose();
      },
      onError: (error: any) => {
        // 409 = duplicate name+category — keep the form open for a rename.
        setSubmitError(error.response?.data?.message || 'Failed to create item');
      },
    },
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<Omit<ServiceCatalogItem, 'id' | 'createdAt' | 'updatedAt' | 'clinicId'>> }) =>
      serviceCatalogService.update(id, data),
    {
      onSuccess: (item) => {
        queryClient.invalidateQueries(['service-catalog']);
        enqueueSnackbar('Pricing item updated', { variant: 'success' });
        onSaved?.(item, { created: false });
        onClose();
      },
      onError: (error: any) => {
        setSubmitError(error.response?.data?.message || 'Failed to update item');
      },
    },
  );

  // Mirrors the backend's resolveSellPrice (service-catalog.service.ts) so the
  // dialog can preview the derived price before the round-trip. Returns null
  // when the required MARKUP inputs aren't filled in yet.
  const computeMarkupPreview = (): number | null => {
    const cost = parseFloat(formData.unitCost);
    const markup = parseFloat(formData.markupPercent);
    if (isNaN(cost) || isNaN(markup)) return null;
    const parsedMin = formData.minPrice !== '' ? parseFloat(formData.minPrice) : 0;
    const min = isNaN(parsedMin) ? 0 : parsedMin;
    const raw = cost * (1 + markup / 100);
    const floored = Math.max(raw, min);
    return Math.round(floored * 100) / 100;
  };

  const handleSubmit = () => {
    setSubmitError(null);
    const parsedAliases = formData.aliases
      ? formData.aliases.split(',').map((a) => a.trim()).filter(Boolean)
      : [];
    const parsedIdexxCodes = formData.idexxTestCodes
      ? formData.idexxTestCodes.split(',').map((c) => c.trim()).filter(Boolean)
      : [];

    const isMarkup = formData.pricingMethod === 'MARKUP';
    const markupPreview = computeMarkupPreview();

    const submitData: any = {
      name: formData.name,
      category: formData.category,
      // MARKUP: basePrice is derived server-side too (resolveSellPrice) — send the
      // live preview so the payload stays consistent even before the round-trip.
      basePrice: isMarkup ? (markupPreview ?? 0) : parseFloat(formData.basePrice),
      unit: formData.unit || undefined,
      // Colours are set from the Schedule only; Service Pricing leaves the key out, keeping a type's colour.
      ...(withColor ? { color: formData.color } : {}),
      aliases: parsedAliases.length > 0 ? parsedAliases : [],
      idexxTestCodes: parsedIdexxCodes,
      isActive: formData.isActive,
      pricingMethod: formData.pricingMethod,
    };

    if (isMarkup) {
      submitData.unitCost = parseFloat(formData.unitCost);
      submitData.markupPercent = parseFloat(formData.markupPercent);
      if (formData.minPrice !== '') {
        submitData.minPrice = parseFloat(formData.minPrice);
      }
    }

    if (editingItem && editingItem.clinicId) {
      updateMutation.mutate({ id: editingItem.id, data: submitData });
    } else {
      createMutation.mutate(submitData as any);
    }
  };

  const isMarkupMethod = formData.pricingMethod === 'MARKUP';
  const markupPreview = computeMarkupPreview();
  // MARKUP requires both inputs it derives basePrice from — the backend 400s
  // otherwise (assertMarkupPricingValid), so gate submit the same way here.
  const isMarkupValid = !isMarkupMethod || (
    formData.unitCost !== '' && !isNaN(parseFloat(formData.unitCost)) && parseFloat(formData.unitCost) >= 0 &&
    formData.markupPercent !== '' && !isNaN(parseFloat(formData.markupPercent)) && parseFloat(formData.markupPercent) >= 0
  );
  // Warn (never block — matches the backend's non-blocking $0 warn in
  // service-catalog.service's create/update) when the resolved price is
  // 0/empty: FIXED reads basePrice directly; MARKUP reads the live preview.
  const hasNoPriceSet = isMarkupMethod
    ? markupPreview === 0
    : formData.basePrice === '' || parseFloat(formData.basePrice) === 0;

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <>
      <DialogTitle>
        {editingItem ? (editingItem.clinicId ? 'Edit Pricing Item' : 'Customize Default Price') : withColor ? 'Add appointment type' : 'Add Pricing Item'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          {editingItem && !editingItem.clinicId && (
            <Alert severity="info" sx={{ mb: 1 }}>
              This will create a custom price for your clinic, overriding the default.
            </Alert>
          )}
          <TextField
            label={withColor ? 'Name' : 'Service Name'}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
          />
          {withColor && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="input"
                type="color"
                value={formData.color}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, color: e.target.value })}
                aria-label="Color on the Schedule"
                sx={{ flex: 'none', width: 36, height: 36, p: 0, border: '1px solid #D1D5DB', borderRadius: 1.5, bgcolor: 'background.paper', cursor: 'pointer' }}
              />
              <Typography variant="body2" color="text.secondary">Color on the Schedule</Typography>
            </Box>
          )}
          <FormControl fullWidth required>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              label="Category"
              disabled={lockCategory || (!!editingItem && !editingItem.clinicId)}
            >
              <MenuItem value="appointment">Appointment</MenuItem>
              <MenuItem value="procedure">Procedure</MenuItem>
              <MenuItem value="medication">Medication</MenuItem>
              <MenuItem value="vaccination">Vaccination</MenuItem>
              <MenuItem value="other">Other (Supplies, Fees, etc.)</MenuItem>
            </Select>
          </FormControl>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Pricing Method
            </Typography>
            <ToggleButtonGroup
              value={formData.pricingMethod}
              exclusive
              size="small"
              onChange={(_, value) => {
                if (value) setFormData({ ...formData, pricingMethod: value });
              }}
            >
              <ToggleButton value="FIXED" sx={{ textTransform: 'none' }}>
                Fixed price
              </ToggleButton>
              <ToggleButton value="MARKUP" sx={{ textTransform: 'none' }}>
                Cost + markup
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {isMarkupMethod && (
            <>
              <TextField
                label="Unit Cost"
                type="number"
                value={formData.unitCost}
                onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                fullWidth
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="What this item costs your clinic per sell unit"
              />
              <TextField
                label="Markup %"
                type="number"
                value={formData.markupPercent}
                onChange={(e) => setFormData({ ...formData, markupPercent: e.target.value })}
                fullWidth
                required
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="e.g., 100 = sell at double the cost"
              />
              <TextField
                label="Min Price (optional)"
                type="number"
                value={formData.minPrice}
                onChange={(e) => setFormData({ ...formData, minPrice: e.target.value })}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Floor applied to the derived price — leave blank for none"
              />
            </>
          )}
          <TextField
            label="Price"
            type="number"
            value={isMarkupMethod ? (markupPreview !== null ? markupPreview.toFixed(2) : '') : formData.basePrice}
            onChange={(e) => setFormData({ ...formData, basePrice: e.target.value })}
            fullWidth
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              readOnly: isMarkupMethod,
            }}
            inputProps={{ min: 0, step: 0.01 }}
            helperText={isMarkupMethod ? 'Derived from cost + markup — read-only' : undefined}
          />
          {hasNoPriceSet && (
            <Alert severity="warning">
              This item has no price — it will bill $0.
            </Alert>
          )}
          {/* freeSolo — the column is free text and live rows already hold
              values outside the list; the suggestions only stop new rows
              fragmenting into mL / ml / cc. */}
          <Autocomplete
            freeSolo
            options={BILLING_UNIT_OPTIONS}
            value={formData.unit}
            onInputChange={(_, value) => setFormData({ ...formData, unit: value })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Priced per (optional)"
                placeholder="e.g. per dose, per mL, per bag"
                helperText="What one unit of this price covers"
              />
            )}
          />
          <TextField
            label="Alternative Names (optional)"
            value={formData.aliases}
            onChange={(e) => setFormData({ ...formData, aliases: e.target.value })}
            fullWidth
            placeholder="e.g., Flunixin Meglumine, Flunixin, Bute"
            helperText="Comma-separated alternative names for automatic price matching"
            size="small"
          />
          <TextField
            label="Linked IDEXX test codes"
            value={formData.idexxTestCodes}
            onChange={(e) => setFormData({ ...formData, idexxTestCodes: e.target.value })}
            fullWidth
            placeholder="e.g., IHD_CBC, IHD_Equine15"
            helperText="Comma-separated IDEXX test code(s) this product bills for. Codes are listed in Settings → Integrations → IDEXX panels."
            size="small"
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.isActive ? 'active' : 'inactive'}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'active' })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            isSaving ||
            !formData.name ||
            isOffline ||
            (isMarkupMethod
              ? !isMarkupValid
              : (formData.basePrice === '' || parseFloat(formData.basePrice) < 0))
          }
        >
          {editingItem ? (editingItem.clinicId ? 'Save Changes' : 'Create Custom Price') : 'Add Item'}
        </Button>
      </DialogActions>
    </>
  );
};

const PricingItemDialog: FC<PricingItemDialogProps> = ({
  open,
  onClose,
  editingItem = null,
  defaultCategory = 'procedure',
  lockCategory = false,
  onSaved,
  withColor = false,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    {open && (
      <PricingItemDialogContent
        key={editingItem?.id ?? 'new'}
        editingItem={editingItem}
        defaultCategory={defaultCategory}
        lockCategory={lockCategory}
        onClose={onClose}
        onSaved={onSaved}
        withColor={withColor}
      />
    )}
  </Dialog>
);

export default PricingItemDialog;
